import { useS3Download } from '@doltech/core/lib/hooks/useS3Download';
import { IconOtherPause } from '@doltech/icons/IconOtherPause';
import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import {
  FlatAudioPlayer,
  FlatPlayerInstance,
} from '@doltech/ui/lib/figma/Audio/Player/FlatAudioPlayer';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { SkillEnum, zIndex } from '@doltech/utils/lib/constants';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { BottomNavigationMobile } from '../mobile/components/BottomNavigationMobile';
import { BottomNavigationReadingMobile } from '../mobile/components/BottomNavigationReadingMobile';
import { TopNavigationMobile } from '../mobile/components/TopNavigationMobile';
import { useTestResultStore } from '../model/test-result.hook';
import { ResultListeningTestSectionInstance } from '../model/TestSection/result-listening-test-section.store';

const AudioPlayerWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  z-index: 20;
`;

const Footer = styled.footer`
  background-color: ${colorsV2.white100};
  padding: 8px 12px;
  box-shadow: 0 -4px 4px ${colorsV2.blackShadow3};
  z-index: 1;
`;

const Header = styled.header`
  background-color: ${colorsV2.white100};
  padding: 8px 12px;
  box-shadow: 0 2px 2px ${colorsV2.blackShadow3};
  z-index: 1;
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  background-color: ${colorsV2.black5};

  .layout-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.HEADER};
  }

  .layout-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.FOOTER};
    background-color: ${colorsV2.white100};
  }
`;

const ListeningBottomMain = styled.div`
  position: relative;
`;

const ReadingbottomNavigation = observer(() => {
  const store = useTestResultStore();
  if (!store.initialed) return null;
  return (
    <Footer>
      <BottomNavigationReadingMobile />
    </Footer>
  );
});

const ListeningBottomNavigation = observer(() => {
  const store = useTestResultStore();
  const currentSection: ResultListeningTestSectionInstance = store?.getCurrentTestSection();
  const s3AudioData = useS3Download(currentSection.script?.audio?.path);
  const audioPlayerRef = React.useRef<FlatPlayerInstance>();
  const [playing, setPlaying] = React.useState(false);

  if (!store.initialed) return null;
  return (
    <ListeningBottomMain>
      <AudioPlayerWrapper className="audio-player-wrapper">
        <Spin size="small" spinning={s3AudioData.isLoading}>
          <FlatAudioPlayer
            ref={audioPlayerRef}
            onPlay={() => {
              setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
            volume={1}
            startTimeInSeconds={currentSection.audioManager.startTimeInSeconds}
            endTimeInSeconds={currentSection.audioManager.endTimeInSeconds}
            onAudioProgress={currentSection.audioManager.progress}
            url={s3AudioData.data?.data?.downloadResourceUrl}
            interactive
          />
        </Spin>
      </AudioPlayerWrapper>
      <Footer className="mobile-footer-info">
        <BottomNavigationMobile
          audioPlayer={
            <Button.Icon
              aria-label="Play audio"
              onClick={() => {
                if (audioPlayerRef.current) {
                  audioPlayerRef.current.playPause();
                  currentSection.audioManager.play();
                }
              }}
              type="secondary"
            >
              {playing ? <IconOtherPause /> : <IconOtherPlay />}
            </Button.Icon>
          }
        />
      </Footer>
    </ListeningBottomMain>
  );
});

export const TestResultLayoutMobile = observer((props: any) => {
  const store = useTestResultStore();
  const { children, onQuit } = props;

  if (!store.initialed) return null;

  return (
    <Main {...props}>
      <Header className="layout-header">
        <TopNavigationMobile onQuit={onQuit} />
      </Header>
      {children}
      <div className="layout-footer">
        {store.skill === SkillEnum.READING ? (
          <ReadingbottomNavigation />
        ) : (
          <ListeningBottomNavigation />
        )}
      </div>
    </Main>
  );
});
